import type { Header, RowData } from '@tanstack/react-table';
import type { MouseEventHandler } from 'react';

import { useCallback, useEffect, useRef, useState } from 'react';

import type { AppTableFeatures } from './Table.hooks';

import { useTableGridContext } from './TableGrid.context';

export const useTableHeaderResize = (header: Header<AppTableFeatures, RowData>) => {
  const { columnStyles, setGridTemplateColumns } = useTableGridContext();

  const headerCellRef = useRef<HTMLDivElement>(null);
  const startPositionRef = useRef<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);

  const onMouseDown = useCallback<MouseEventHandler>((e) => {
    startPositionRef.current = e.screenX;
    setIsResizing(true);
  }, []);

  const onMouseUp = useCallback(() => {
    setIsResizing(false);
    startPositionRef.current = null;
  }, []);

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      const headerElement = headerCellRef.current;

      if (!headerElement) return;

      window.requestAnimationFrame(() => {
        if (startPositionRef.current === null) return; // Mouse has already been released before this RAF callback is executed

        const offset = e.screenX - startPositionRef.current;

        // Note: This resizing logic assumes only a single header group is used,
        // because it uses `header.index` to compare positions.
        setGridTemplateColumns((prev) => {
          // Measure the current pixel width of every column so we can work from
          // the actual rendered layout rather than the (possibly `fr`-based) template.
          const currentWidths = prev.map((_, i) => {
            const el = headerElement.parentElement?.children[i];
            return el?.getBoundingClientRect()?.width ?? 0;
          });

          // Walk from the resized column leftwards. When shrinking, each column
          // gives up width down to its min, then the leftover cascades to its
          // left neighbour, and so on. When growing, the resized column takes
          // the full offset and nothing cascades.
          const indices = Array.from({ length: header.index + 1 }, (_, i) => header.index - i);

          const { widths } = indices.reduce(
            (acc, i) => {
              if (acc.remaining === 0) return acc;

              if (acc.remaining > 0) {
                const widths = acc.widths.map((width, j) => (j === i ? width + acc.remaining : width));
                return { remaining: 0, widths };
              }

              const minWidth = columnStyles[i].minSize ?? 0;
              const shrink = Math.max(0, Math.min(acc.widths[i] - minWidth, -acc.remaining));
              const widths = acc.widths.map((width, j) => (j === i ? width - shrink : width));
              return { remaining: acc.remaining + shrink, widths };
            },
            { remaining: offset, widths: currentWidths },
          );

          // Freeze every affected column to a fixed pixel width; leave the
          // columns to the right of the resized column untouched.
          return prev.map((value, i) => (i > header.index ? value : `${widths[i]}px`));
        });

        startPositionRef.current = e.screenX;
      });
    },
    [columnStyles, header.index, setGridTemplateColumns],
  );

  useEffect(() => {
    if (!isResizing) return;

    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mousemove', onMouseMove);

    return () => {
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [isResizing, onMouseMove, onMouseUp]);

  return { headerCellRef, isResizing, onMouseDown };
};
